import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useApiClient } from "../apiClientContext";
import { ApiError } from "../auth/apiClient";

const QUEUE_MUTES_KEY = ["push", "queue-mutes"] as const;

const QueueMutesResponseSchema = z.object({
    mutedQueueIds: z.array(z.string()),
});

type QueueMutesResponse = z.infer<typeof QueueMutesResponseSchema>;

interface MutationContext {
    readonly previous: QueueMutesResponse | undefined;
}

export interface UseQueueNotificationPreferencesResult {
    /** Queue ids the user has muted; empty while loading or when unavailable. */
    readonly mutedQueueIds: ReadonlySet<string>;
    readonly isLoading: boolean;
    /** Flip the mute state for one queue (optimistic). */
    toggleMute: (queueId: string) => void;
}

const EMPTY: ReadonlySet<string> = new Set();

/**
 * Per-user, per-queue push-notification mute preferences. This is the second
 * control layer beneath the master device toggle: muting a queue here stops
 * pushes for that queue across all the user's devices. Independent of whether
 * a given device is currently subscribed.
 *
 * `enabled` gates the network call — pass the push feature-availability flag so
 * environments without push provisioned never hit the endpoint.
 */
export function useQueueNotificationPreferences(
    enabled: boolean,
): UseQueueNotificationPreferencesResult {
    const client = useApiClient();
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: QUEUE_MUTES_KEY,
        queryFn: async (): Promise<QueueMutesResponse> => {
            const response = await client.fetch("/push/queue-mutes");
            if (!response.ok) {
                throw new ApiError(
                    response.status,
                    response.statusText,
                    `Failed to fetch queue notification preferences: ${String(response.status)}`,
                );
            }
            const json: unknown = await response.json();
            return QueueMutesResponseSchema.parse(json);
        },
        enabled,
        staleTime: 60_000,
    });

    const mutedQueueIds = useMemo<ReadonlySet<string>>(
        () => (query.data === undefined ? EMPTY : new Set(query.data.mutedQueueIds)),
        [query.data],
    );

    const mutation = useMutation<unknown, Error, { queueId: string; mute: boolean }, MutationContext>({
        mutationFn: async ({ queueId, mute }): Promise<void> => {
            const response = await client.fetch(
                `/push/queue-mutes/${encodeURIComponent(queueId)}`,
                { method: mute ? "PUT" : "DELETE" },
            );
            if (!response.ok) {
                throw new ApiError(
                    response.status,
                    response.statusText,
                    `Failed to update queue notification preference: ${String(response.status)}`,
                );
            }
        },
        onMutate: async ({ queueId, mute }): Promise<MutationContext> => {
            await queryClient.cancelQueries({ queryKey: QUEUE_MUTES_KEY });
            const previous = queryClient.getQueryData<QueueMutesResponse>(QUEUE_MUTES_KEY);
            const next = new Set(previous?.mutedQueueIds ?? []);
            if (mute) {
                next.add(queueId);
            } else {
                next.delete(queueId);
            }
            queryClient.setQueryData<QueueMutesResponse>(QUEUE_MUTES_KEY, {
                mutedQueueIds: [...next],
            });
            return { previous };
        },
        onError: (_error, _variables, context): void => {
            if (context?.previous !== undefined) {
                queryClient.setQueryData(QUEUE_MUTES_KEY, context.previous);
            }
        },
        onSettled: (): void => {
            void queryClient.invalidateQueries({ queryKey: QUEUE_MUTES_KEY });
        },
    });

    const toggleMute = useCallback(
        (queueId: string): void => {
            mutation.mutate({ queueId, mute: !mutedQueueIds.has(queueId) });
        },
        [mutation, mutedQueueIds],
    );

    return {
        mutedQueueIds,
        isLoading: query.isLoading,
        toggleMute,
    };
}
